import React, { FC, useState, useEffect } from 'react';
import {
  IReport,
  IFunnelStageData,
  fetchReportData,
  deleteReport
} from '../../../api';
import { useWebsite } from '../../../hooks/useWebsite';
import { useDateRange } from '../../../hooks/useDateRange';
import { FunnelChart, FunnelDetails } from '../funnel';
import DeleteButton from '../DeleteButton';

interface Props {
  report: IReport;
}

const ReportCard: FC<Props> = ({ report }) => {
  const { websiteId } = useWebsite();
  const { from, to, timezone } = useDateRange();
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Array<IFunnelStageData>>([]);
  const [deleted, setDeleted] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState(false);

  const toggleExpand = () => setIsExpanded(!isExpanded);

  const fetchData = async () => {
    if (!isExpanded) {
      return null;
    }
    try {
      setLoading(true);
      setData(await fetchReportData(websiteId, report.id, { from, to, timezone }));
    } catch (e) {
      console.log('error loading report', e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [from, to, isExpanded]);

  useEffect(() => {
    if (deleted) {
      setShowAlert(true);
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [deleted]);

  if (deleted && showAlert) {
    return (
      <div className="alert alert-success alert-dismissible fade show my-5" role="alert">
        <i className="bi bi-check-circle-fill text-success me-2"></i>
        <strong>The report [{report.name}] has been deleted.</strong>
      </div>
    );
  }

  if (deleted && !showAlert) {
    return null;
  }

  return (
    <div className="card my-5">
      <div className="card-header" role="button" onClick={toggleExpand}>
        <div className='card-title align-items-start flex-column'>
          <span className="card-label fw-bold fs-3 mb-1">
            <span className="badge badge-primary me-2">{report.type}</span>
            {report.name}
          </span>
        </div>
        <div className="card-toolbar">
          <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} fs-3`}></i>
        </div>
      </div>
      {isExpanded && (
        <>
          <div className='card-body px-5 py-2'>
            {loading ? (
              <>Loading</>
            ) : (
              <div className="row">
                <div className="col-lg-6 col-md-12 ">
                  <FunnelChart
                    options={{ showLegend: true }}
                    data={data}
                  />
                </div>
                <div className="col-lg-6 col-md-12 ">
                  <FunnelDetails data={data} />
                </div>
              </div>
            )}
          </div>
          <div className="card-footer text-center">
            <DeleteButton
              label="Delete report"
              delay={3000}
              onDelete={() => {
                deleteReport(websiteId, report.id);
                setDeleted(true);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ReportCard;
